// Bundle-selling helpers shared by NewSalePage and POSPage.
//
// Data model recap (migrations 080, 097, 098, 099):
//   - product_bundles is a RECIPE: name + is_active flag.
//   - bundle_items joins a bundle to its components, each row holding
//     product_id, quantity-per-bundle, unit_price.
//   - Bundles have NO price column — the selling price is derived as
//       SUM(bundle_items.unit_price * quantity)
//     and bundle cost is NEVER stored (live-looked-up at sale time).
//   - When a bundle is sold the cart EXPANDS it into real component lines
//     and writes sales_order_items.bundle_id / bundle_name (snapshot labels,
//     no FK — bundle may later be deleted/edited; past sales unaffected).
//
// Everything here is pure data shaping plus one React Query hook. No UI.

import { useQuery } from '@tanstack/react-query'
import { supabase } from './supabase'

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const db = (t: string): any => (supabase as any).from(t)

// ─── Types ────────────────────────────────────────────────────────────────────

export type BundleComponent = {
  product_id: string
  product_name: string
  unit_qty: number          // bundle_items.quantity — per ONE bundle
  unit_price: number        // bundle_items.unit_price — selling price per unit
  is_vatable: boolean       // resolved from the component product's vat_categories
}

export type ActiveBundle = {
  id: string
  name: string
  description: string | null
  components: BundleComponent[]
}

// Shape of an expanded cart-line component sent to post_sale_atomic /
// post_sale_return_atomic via the p_items array. Caller is responsible for
// resolving tax_amount / price_inc_tax / discount allocation per component.
export type ExpandedComponentLine = {
  product_id: string
  product_name: string
  quantity: number          // cartQty * unit_qty
  unit_price: number
  bundle_id: string
  bundle_name: string
}

// ─── Hook: active bundles for a location ──────────────────────────────────────
//
// Returns active product_bundles joined with their bundle_items rows, plus
// each component product's is_vatable flag — everything the caller needs to
// price the bundle, check availability, and resolve per-component VAT.
//
// The result does NOT depend on the location (the recipe is global). The
// locationId parameter is accepted for API symmetry with the stock query and
// to make consumer call-sites read naturally; it does NOT trigger refetch.

export function useActiveBundlesForLocation(opts: {
  tenantId: string
  locationId: string | null | undefined
}) {
  const { tenantId } = opts
  return useQuery<ActiveBundle[]>({
    queryKey: ['active-bundles', tenantId],
    enabled: !!tenantId,
    staleTime: 60_000,
    queryFn: async () => {
      const { data, error } = await db('product_bundles')
        .select(
          'id, name, description, is_active, ' +
          'bundle_items(id, product_id, quantity, unit_price, ' +
          '  products(id, name, vat_categories(is_vatable)))',
        )
        .eq('tenant_id', tenantId)
        .eq('is_active', true)
        .order('name')
      if (error) throw error

      type Row = {
        id: string
        name: string
        description: string | null
        bundle_items: Array<{
          id: string
          product_id: string
          quantity: number
          unit_price: number
          products: { id: string; name: string; vat_categories: { is_vatable: boolean } | null } | null
        }>
      }

      return (data ?? []).map((b: Row): ActiveBundle => ({
        id: b.id,
        name: b.name,
        description: b.description,
        components: (b.bundle_items ?? [])
          // Skip rows whose component product has been deleted — the bundle
          // would otherwise expand into a row that fails the products FK.
          .filter(it => !!it.products && !!it.product_id)
          .map(it => ({
            product_id: it.product_id,
            product_name: it.products!.name,
            unit_qty: Number(it.quantity),
            unit_price: Number(it.unit_price),
            is_vatable: it.products!.vat_categories?.is_vatable ?? false,
          })),
      }))
    },
  })
}

// ─── Pricing ──────────────────────────────────────────────────────────────────

export function bundleDerivedPrice(bundle: ActiveBundle): number {
  return bundle.components.reduce((s, c) => s + c.unit_qty * c.unit_price, 0)
}

// ─── Availability ─────────────────────────────────────────────────────────────
//
// How many copies of the bundle can be assembled from the per-location stock,
// using the component recipe's unit_qty as the divisor for each component.
// Returns 0 if any component is missing from the stock map (no current_stock
// row at this location), or if the bundle has no components.

export function bundleAvailableQty(
  bundle: ActiveBundle,
  stockByProductIdAtLocation: Record<string, number>,
): number {
  if (bundle.components.length === 0) return 0
  let min = Infinity
  for (const c of bundle.components) {
    if (c.unit_qty <= 0) return 0
    const stock = stockByProductIdAtLocation[c.product_id] ?? 0
    const possible = Math.floor(stock / c.unit_qty)
    if (possible < min) min = possible
  }
  return min === Infinity ? 0 : Math.max(0, min)
}

// ─── Decomposition ────────────────────────────────────────────────────────────
//
// Snapshot expansion. cartQty = how many of this bundle the customer is
// buying. Each component line gets its own quantity (cartQty * unit_qty)
// and carries the bundle_id / bundle_name tags so the line can be grouped
// under the bundle heading at display time.
//
// The returned objects are intentionally minimal — they DO NOT carry tax,
// discount, or price_inc_tax. The caller (NewSalePage / POSPage) computes
// those per component based on the active VAT mode + vatRate + any
// line-level discount allocation, then maps them into the RPC payload.

export function expandBundleToComponentLines(
  bundle: ActiveBundle,
  cartQty: number,
): ExpandedComponentLine[] {
  return bundle.components.map(c => ({
    product_id: c.product_id,
    product_name: c.product_name,
    quantity: cartQty * c.unit_qty,
    unit_price: c.unit_price,
    bundle_id: bundle.id,
    bundle_name: bundle.name,
  }))
}
